import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import useContentProtection from '../../hooks/useContentProtection';
import RPATOnboarding from './RPATOnboarding';
import { ResumeBanner } from '../../components/gamification';
import JobCategorySelectionModal from '../../components/rpat/JobCategorySelectionModal';
import AdminResetButton from '../../components/rpat/AdminResetButton';
import MiniCommunicationScoreCard from '../../components/communication/MiniCommunicationScoreCard';
import { 
  BookOpen, 
  Play, 
  Trophy, 
  Clock, 
  Target, 
  ChevronRight,
  Award,
  Mic,
  CheckCircle2,
  Lock,
  Keyboard,
  GraduationCap,
  Sparkles,
  TrendingUp,
  BarChart3,
  Loader2,
  Briefcase,
  Search,
  Users,
  RefreshCw,
  History
} from 'lucide-react';

const RPATDashboard = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  // Apply content protection
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  
  // Advanced certification state (Phases 5-8)
  const [certificationStatus, setCertificationStatus] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [globalProgress, setGlobalProgress] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/rpat/dashboard');
      setDashboard(res.data);
      
      // Fetch certification status for Phases 5-8
      try {
        const certRes = await api.get('/rpat/certification/status');
        setCertificationStatus(certRes.data);
        
        // Show category selection modal if Phase 4 is complete but no category selected
        if (certRes.data.show_category_selection) {
          setShowCategoryModal(true);
        }
      } catch (certErr) {
        // Certification status may not be available yet (user hasn't completed Phase 4)
        console.log('Certification status not available yet');
      }
      
      // Fetch global 8-phase progress
      try {
        const globalRes = await api.get('/rpat/certification/global-progress');
        setGlobalProgress(globalRes.data);
      } catch (globalErr) {
        console.log('Global progress not available yet');
      }
      
      // Check if user has completed onboarding (server-side first, then localStorage fallback)
      let hasCompletedOnboarding = false;
      try {
        const onboardingRes = await api.get('/rpat/topics/onboarding/status');
        hasCompletedOnboarding = onboardingRes.data?.completed === true;
      } catch (onboardingErr) {
        // Fallback to localStorage if server check fails
        const onboardingKey = `rpat_onboarding_${user?.id}`;
        hasCompletedOnboarding = !!localStorage.getItem(onboardingKey);
      }
      
      // Show onboarding for enrolled users who haven't completed it yet
      // and have no significant progress
      const hasProgress = res.data?.progress?.completed_topics > 0;
      
      if (res.data?.enrolled && !hasCompletedOnboarding && !hasProgress) {
        setShowOnboarding(true);
      } else {
        setOnboardingComplete(true);
      }
    } catch (err) {
      console.error('Failed to fetch R-PAT dashboard:', err);
      setError('Failed to load R-PAT dashboard');
    } finally {
      setLoading(false);
    }
  }, [api, user?.id]);

  const handleEnroll = async () => {
    try {
      setEnrolling(true);
      await api.post('/rpat/courses/rpat-001/enroll');
      toast.success('Successfully enrolled in R-PAT Training!');
      // After enrollment, show onboarding
      setShowOnboarding(true);
      await fetchDashboard();
    } catch (err) {
      console.error('Enrollment failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to enroll. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };
  
  const handleOnboardingComplete = async (userData) => {
    // Save onboarding completion to server (primary) and localStorage (backup)
    try {
      await api.post('/rpat/topics/onboarding/complete', {
        background: userData?.background || 'new_to_recruitment',
        goals: userData?.goals || []
      });
    } catch (err) {
      console.error('Failed to save onboarding to server:', err);
    }
    
    // Also save to localStorage as backup
    const onboardingKey = `rpat_onboarding_${user?.id}`;
    localStorage.setItem(onboardingKey, JSON.stringify({
      completedAt: new Date().toISOString(),
      ...userData
    }));
    
    setShowOnboarding(false);
    setOnboardingComplete(true);
  };

  const handleCategorySelected = (data) => {
    setShowCategoryModal(false);
    fetchDashboard();
  };

  const [restoringProgress, setRestoringProgress] = useState(false);

  const handleRestoreProgress = async () => {
    try {
      setRestoringProgress(true);
      const res = await api.post('/rpat/certification/restore-progress');
      
      if (res.data.changes && res.data.changes.length > 0) {
        toast.success('Progress restored!', {
          description: res.data.changes.join(', '),
          duration: 5000
        });
        // Refresh dashboard
        fetchDashboard();
      } else {
        toast.info('No changes needed - your progress is correct');
      }
    } catch (err) {
      console.error('Failed to restore progress:', err);
      toast.error('Failed to restore progress');
    } finally {
      setRestoringProgress(false);
    }
  };

  const handleChangeCategory = async () => {
    if (!window.confirm('Are you sure? This will reset all your certification progress for Phases 5-8.')) {
      return;
    }

    try {
      await api.post('/rpat/certification/change-category');
      toast.success('Category selection reset');
      setShowCategoryModal(true);
      fetchDashboard();
    } catch (err) {
      console.error('Failed to change category:', err);
      toast.error('Failed to reset category');
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const getPhaseIcon = (phaseName, phaseIndex) => {
    // Phase 1-4 icons
    if (phaseIndex < 4) {
      switch (phaseName) {
        case 'Foundation': return BookOpen;
        case 'Core Skills': return Target;
        case 'Communication & Interview': return Mic;
        case 'Practical Application': return GraduationCap;
        default: return BookOpen;
      }
    }
    // Phase 5-8 icons
    switch (phaseIndex) {
      case 4: return Briefcase;    // Phase 5: Job Category Mastery
      case 5: return Search;       // Phase 6: Sourcing Labs
      case 6: return Users;        // Phase 7: Role Play Lab
      case 7: return Award;        // Phase 8: Final Certification
      default: return BookOpen;
    }
  };

  const getPhaseColor = (index) => {
    const colors = [
      'from-cyan-500 to-blue-600',     // Phase 1
      'from-violet-500 to-purple-600', // Phase 2
      'from-amber-500 to-orange-600',  // Phase 3
      'from-emerald-500 to-green-600', // Phase 4
      'from-pink-500 to-rose-600',     // Phase 5
      'from-indigo-500 to-blue-600',   // Phase 6
      'from-teal-500 to-cyan-600',     // Phase 7
      'from-yellow-500 to-amber-600'   // Phase 8
    ];
    return colors[index % colors.length];
  };

  const getPhaseBgColor = (index) => {
    const bgColors = [
      'bg-cyan-50 border-cyan-200',
      'bg-violet-50 border-violet-200',
      'bg-amber-50 border-amber-200',
      'bg-emerald-50 border-emerald-200',
      'bg-pink-50 border-pink-200',
      'bg-indigo-50 border-indigo-200',
      'bg-teal-50 border-teal-200',
      'bg-yellow-50 border-yellow-200'
    ];
    return bgColors[index % bgColors.length];
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      </MainLayout>
    );
  }
  
  // Show onboarding for first-time enrolled users
  if (showOnboarding && dashboard?.enrolled) {
    return (
      <RPATOnboarding 
        onComplete={handleOnboardingComplete}
        userName={user?.name?.split(' ')[0]}
      />
    );
  }

  if (error || !dashboard?.enrolled) {
    return (
      <MainLayout>
        <div className="p-6">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="pt-12 pb-8 text-center">
              <GraduationCap className="h-16 w-16 mx-auto text-cyan-600 mb-4" />
              <h2 className="text-2xl font-bold mb-2">R-PAT: Recruitment Process Autonomous Training</h2>
              <p className="text-muted-foreground mb-6">
                Transform into a confident, skilled recruiter with our comprehensive 53-hour voice-based training program.
              </p>
              <Button 
                onClick={handleEnroll}
                disabled={enrolling}
                data-testid="rpat-enroll-btn"
              >
                {enrolling ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enrolling...
                  </>
                ) : (
                  <>
                    <Play className="mr-2 h-4 w-4" /> Start Your Journey
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const { course, progress, current_topic, next_topic, phase_progress, typing_tests_completed } = dashboard;
  
  // Determine if there's an incomplete lesson to resume
  const hasIncompleteLesson = current_topic && current_topic.id;
  const resumeLesson = current_topic || next_topic;

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Resume Banner - Shows if user has an in-progress lesson */}
        {hasIncompleteLesson && resumeLesson && (
          <ResumeBanner
            sessionName={`${resumeLesson.code} - ${resumeLesson.title}`}
            progress={Math.round((progress.completed_topics / progress.total_topics) * 100)}
            lastActive="your last session"
            onResume={() => navigate(`/rpat/lesson/${resumeLesson.id}`)}
            onStartNew={() => {}}
          />
        )}
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              R-PAT Training
            </h1>
            <p className="text-muted-foreground mt-1">
              Recruitment Process Autonomous Training
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Admin Reset Button */}
            {user?.role === 'admin' && (
              <AdminResetButton api={api} onReset={fetchDashboard} />
            )}
            {next_topic && (
              <Button 
                size="lg"
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                onClick={() => navigate(`/rpat/lesson/${next_topic.id}`)}
              >
                <Play className="mr-2 h-5 w-5" />
                {current_topic ? 'Continue Learning' : 'Start Learning'}
              </Button>
            )}
          </div>
        </div>

        {/* Progress Summary Card - All 8 Phases */}
        {globalProgress?.enrolled && (
          <Card className="overflow-hidden border-2 border-cyan-200 bg-gradient-to-r from-slate-900 via-cyan-900 to-blue-900 text-white">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Progress Ring */}
                <div className="flex items-center gap-6">
                  <div className="relative w-28 h-28">
                    {/* Background circle */}
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="56"
                        cy="56"
                        r="48"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        className="text-white/20"
                      />
                      <circle
                        cx="56"
                        cy="56"
                        r="48"
                        stroke="url(#progressGradient)"
                        strokeWidth="8"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={`${(globalProgress.total_progress_percentage / 100) * 301.59} 301.59`}
                      />
                      <defs>
                        <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#06b6d4" />
                          <stop offset="100%" stopColor="#3b82f6" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold">{globalProgress.total_progress_percentage}%</span>
                      <span className="text-xs text-cyan-300">Complete</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Overall Progress</h3>
                    <p className="text-cyan-300 text-sm">All 8 Phases Combined</p>
                    {globalProgress.specialization && (
                      <Badge className="mt-2 bg-cyan-500/30 text-cyan-200 border border-cyan-400/30">
                        {globalProgress.specialization}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 rounded-lg p-3 backdrop-blur">
                    <div className="flex items-center gap-2 mb-1">
                      <BookOpen className="h-4 w-4 text-cyan-400" />
                      <span className="text-xs text-cyan-300">Topics</span>
                    </div>
                    <p className="text-xl font-bold">
                      {globalProgress.stats?.topics_completed || 0}
                      <span className="text-sm font-normal text-white/60">/{globalProgress.stats?.topics_total || 0}</span>
                    </p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 backdrop-blur">
                    <div className="flex items-center gap-2 mb-1">
                      <Briefcase className="h-4 w-4 text-pink-400" />
                      <span className="text-xs text-pink-300">Jobs</span>
                    </div>
                    <p className="text-xl font-bold">
                      {globalProgress.stats?.jobs_completed || 0}
                      <span className="text-sm font-normal text-white/60">/5</span>
                    </p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 backdrop-blur">
                    <div className="flex items-center gap-2 mb-1">
                      <Search className="h-4 w-4 text-amber-400" />
                      <span className="text-xs text-amber-300">Labs</span>
                    </div>
                    <p className="text-xl font-bold">
                      {globalProgress.stats?.labs_completed || 0}
                      <span className="text-sm font-normal text-white/60">/5</span>
                    </p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 backdrop-blur">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="h-4 w-4 text-emerald-400" />
                      <span className="text-xs text-emerald-300">Role Plays</span>
                    </div>
                    <p className="text-xl font-bold">
                      {globalProgress.stats?.scenarios_completed || 0}
                      <span className="text-sm font-normal text-white/60">/5</span>
                    </p>
                  </div>
                </div>

                {/* Time & Badges */}
                <div className="space-y-4">
                  {/* Time Estimate */}
                  <div className="bg-white/10 rounded-lg p-3 backdrop-blur">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-amber-400" />
                        <span className="text-sm">Time Progress</span>
                      </div>
                      <span className="text-xs text-white/60">
                        {globalProgress.time?.spent_hours || 0}h / {globalProgress.time?.total_hours || 53}h
                      </span>
                    </div>
                    <Progress 
                      value={(globalProgress.time?.spent_hours / globalProgress.time?.total_hours) * 100 || 0} 
                      className="h-2 bg-white/20" 
                    />
                    <p className="text-xs text-cyan-300 mt-1">
                      ~{globalProgress.time?.estimated_remaining_hours || 0}h remaining
                    </p>
                  </div>

                  {/* Badges Earned */}
                  <div className="bg-white/10 rounded-lg p-3 backdrop-blur">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Award className="h-4 w-4 text-amber-400" />
                        <span className="text-sm">Badges Earned</span>
                      </div>
                      <span className="font-bold text-amber-400">
                        {globalProgress.badges_earned?.length || 0}/{globalProgress.badges_total || 8}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {globalProgress.badges_earned?.slice(0, 4).map((badge, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs bg-amber-500/20 text-amber-300 border-amber-400/30">
                          {badge}
                        </Badge>
                      ))}
                      {(globalProgress.badges_earned?.length || 0) > 4 && (
                        <Badge variant="outline" className="text-xs bg-white/10 text-white/60">
                          +{globalProgress.badges_earned.length - 4} more
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Certified Banner */}
              {globalProgress.stats?.is_certified && (
                <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-center gap-3">
                  <Trophy className="h-6 w-6 text-amber-400" />
                  <span className="text-lg font-semibold text-amber-300">
                    TekWissen Certified Recruiter
                  </span>
                  <Trophy className="h-6 w-6 text-amber-400" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Communication Score Card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <MiniCommunicationScoreCard moduleName="rpat" className="h-full" />
          </div>
          <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Typing Tests</p>
                  <p className="text-2xl font-bold text-cyan-700">{typing_tests_completed}/10</p>
                </div>
                <Keyboard className="h-8 w-8 text-cyan-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Progress</p>
                  <p className="text-2xl font-bold text-cyan-700">{Math.round(progress.percentage)}%</p>
                </div>
                <TrendingUp className="h-8 w-8 text-cyan-600" />
              </div>
              <Progress value={progress.percentage} className="mt-2 h-2" />
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Topics Completed</p>
                  <p className="text-2xl font-bold text-violet-700">
                    {progress.completed_topics}/{progress.total_topics}
                  </p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-violet-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Time Spent</p>
                  <p className="text-2xl font-bold text-amber-700">{progress.time_spent_hours}h</p>
                </div>
                <Clock className="h-8 w-8 text-amber-600" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">of {course.total_hours}h total</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Sessions</p>
                  <p className="text-2xl font-bold text-emerald-700">{progress.completed_topics}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Current/Next Topic Card */}
        {(current_topic || next_topic) && (
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-6 text-white">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-5 w-5" />
                <span className="text-sm font-medium opacity-90">
                  {current_topic ? 'Continue Your Lesson' : 'Start Next Topic'}
                </span>
              </div>
              <h3 className="text-2xl font-bold mb-1">
                {(current_topic || next_topic).code} - {(current_topic || next_topic).title}
              </h3>
              <p className="opacity-80 text-sm">{(current_topic || next_topic).description}</p>
            </div>
            <CardContent className="pt-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-6 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {(current_topic || next_topic).duration_minutes} min
                  </span>
                  <span className="flex items-center gap-1">
                    <Mic className="h-4 w-4" />
                    Voice-based lesson
                  </span>
                  {(current_topic || next_topic).typing_test_number && (
                    <span className="flex items-center gap-1">
                      <Keyboard className="h-4 w-4" />
                      Typing test included
                    </span>
                  )}
                </div>
                <Button 
                  onClick={() => navigate(`/rpat/lesson/${(current_topic || next_topic).id}`)}
                  className="bg-gradient-to-r from-cyan-600 to-blue-600"
                >
                  {current_topic ? 'Continue' : 'Begin'} <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Phase Progress - All 8 Phases */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Learning Phases</h2>
            {certificationStatus?.selected_job_category && (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200 text-cyan-800">
                  <Briefcase className="h-3 w-3 mr-1" />
                  {certificationStatus.selected_job_category_name}
                </Badge>
                <Button variant="ghost" size="sm" onClick={handleChangeCategory} className="h-7 px-2">
                  <RefreshCw className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
          
          {/* Combined 8-Phase Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Phases 1-4: Foundation Training */}
            {phase_progress.map((phase, index) => {
              const Icon = getPhaseIcon(phase.name, index);
              // Admins bypass all locks
              const isAdmin = user?.role === 'admin';
              // Check previous phase completion using progress_percentage OR completed topics count
              const prevPhase = index > 0 ? phase_progress[index - 1] : null;
              const prevPhaseComplete = !prevPhase || 
                (prevPhase.progress_percentage >= 100) || 
                (prevPhase.total_topics > 0 && prevPhase.completed_topics >= prevPhase.total_topics);
              const isLocked = !isAdmin && index > 0 && !prevPhaseComplete;
              const progressPct = phase.progress_percentage;
              
              const handlePhaseClick = () => {
                if (isLocked) {
                  // Show preview toast but don't block - let them see content overview
                  toast.info(`Complete Phase ${index} first to unlock full access. Viewing preview...`);
                }
                navigate(`/rpat/phase/${phase.id}`);
              };
              
              return (
                <Card 
                  key={phase.id}
                  className={`relative overflow-hidden transition-all hover:shadow-lg cursor-pointer ${
                    isLocked ? 'opacity-70 grayscale-[30%]' : ''
                  } ${getPhaseBgColor(index)}`}
                  onClick={handlePhaseClick}
                  data-testid={`phase-card-${index + 1}`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${getPhaseColor(index)} opacity-5`} />
                  <CardContent className="pt-4 relative">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${getPhaseColor(index)}`}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      {isLocked ? (
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600">Preview</Badge>
                          <Lock className="h-4 w-4 text-muted-foreground" />
                        </div>
                      ) : progressPct === 100 ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                      ) : null}
                    </div>
                    <h3 className="font-semibold mb-1">Phase {index + 1}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{phase.name}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>{phase.completed_topics}/{phase.total_topics} topics</span>
                      <span>{Math.round(progressPct)}%</span>
                    </div>
                    <Progress value={progressPct} className="h-1.5" />
                  </CardContent>
                </Card>
              );
            })}
            
            {/* Restore Progress Banner - Show when phases 5-8 seem incorrectly locked */}
            {certificationStatus && (
              certificationStatus.phases?.phase_5?.status === 'locked' ||
              certificationStatus.phases?.phase_6?.status === 'locked' ||
              certificationStatus.phases?.phase_7?.status === 'locked'
            ) && (
              <Card className="border-amber-300 bg-amber-50/50 dark:bg-amber-950/20">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/50">
                        <History className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium">Progress locked unexpectedly?</p>
                        <p className="text-sm text-muted-foreground">
                          If you completed phases previously but they now show as locked, click Restore to fix it.
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleRestoreProgress}
                      disabled={restoringProgress}
                      className="border-amber-300 hover:bg-amber-100 shrink-0"
                    >
                      {restoringProgress ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Restoring...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Restore Progress
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Phases 5-8: Advanced Certification */}
            {[
              { key: 'phase_5', name: 'Job Category Mastery', desc: 'Master 5 jobs from your category', icon: Briefcase, progressKey: 'completed_jobs', total: 5, unit: 'jobs', route: '/rpat/certification/job-mastery' },
              { key: 'phase_6', name: 'Sourcing Labs', desc: 'Complete 5 sourcing challenges', icon: Search, progressKey: 'completed_labs', total: 5, unit: 'labs', route: '/rpat/certification/sourcing-labs' },
              { key: 'phase_7', name: 'Role Play Lab', desc: 'Complete 5 role play scenarios', icon: Users, progressKey: 'completed_scenarios', total: 5, unit: 'scenarios', route: '/rpat/certification/roleplay' },
              { key: 'phase_8', name: 'Final Certification', desc: 'Earn your certificate', icon: Award, progressKey: null, total: 1, unit: 'assessment', route: '/rpat/certification/final' }
            ].map((advPhase, idx) => {
              const phaseIndex = idx + 4; // 4, 5, 6, 7 for array index (Phase 5, 6, 7, 8)
              const Icon = advPhase.icon;
              const phaseData = certificationStatus?.phases?.[advPhase.key] || {};
              const phaseStatus = phaseData.status || 'locked';
              
              // Admins bypass all locks
              const isAdmin = user?.role === 'admin';
              
              // Determine if locked - Phase 5 needs Phase 4 complete, others need previous advanced phase
              const phase4Data = phase_progress?.[3];
              const isPhase4Complete = phase4Data && (
                (phase4Data.progress_percentage >= 100) || 
                (phase4Data.total_topics > 0 && phase4Data.completed_topics >= phase4Data.total_topics)
              );
              let isLocked = false;
              if (!isAdmin) {
                if (phaseIndex === 4) {
                  // Phase 5 is locked if Phase 4 is not complete
                  isLocked = !isPhase4Complete;
                } else {
                  // Phases 6-8 are locked based on certification status
                  isLocked = phaseStatus === 'locked';
                }
              }
              
              const isCompleted = phaseStatus === 'completed';
              const isInProgress = phaseStatus === 'in_progress';
              const isAvailable = phaseStatus === 'available';
              
              // Calculate progress
              let progressValue = 0;
              let progressText = '';
              
              if (advPhase.key === 'phase_5') {
                const completed = phaseData.completed_jobs || 0;
                progressValue = (completed / 5) * 100;
                progressText = `${completed}/5 ${advPhase.unit}`;
              } else if (advPhase.key === 'phase_6') {
                const completed = phaseData.completed_labs || 0;
                progressValue = (completed / 5) * 100;
                progressText = `${completed}/5 ${advPhase.unit}`;
              } else if (advPhase.key === 'phase_7') {
                const completed = phaseData.completed_scenarios || 0;
                progressValue = (completed / 5) * 100;
                progressText = `${completed}/5 ${advPhase.unit}`;
              } else if (advPhase.key === 'phase_8') {
                progressValue = phaseData.certificate_id ? 100 : (phaseData.assessment_score ? 50 : 0);
                progressText = phaseData.certificate_id ? 'Certified!' : (phaseData.assessment_score ? `Score: ${phaseData.assessment_score}%` : 'Not started');
              }
              
              // Handle phase click - allow preview for locked phases
              const handlePhaseClick = () => {
                // If Phase 5 and no category selected (and not admin)
                if (phaseIndex === 4 && !certificationStatus?.selected_job_category && !isAdmin) {
                  if (isLocked) {
                    // Locked + no category - show toast and still navigate to preview
                    toast.info('Complete Phase 4 first to unlock. Viewing preview...');
                    navigate(advPhase.route);
                    return;
                  } else {
                    // Unlocked but no category - show modal to select category
                    setShowCategoryModal(true);
                    return;
                  }
                }
                
                if (isLocked) {
                  // Show preview toast but navigate anyway for overview
                  if (phaseIndex === 4) {
                    toast.info('Complete Phase 4 to unlock. Viewing preview...');
                  } else {
                    toast.info('Complete the previous phase to unlock. Viewing preview...');
                  }
                }
                
                navigate(advPhase.route);
              };
              
              return (
                <Card 
                  key={advPhase.key}
                  className={`relative overflow-hidden transition-all hover:shadow-lg cursor-pointer ${
                    isLocked ? 'opacity-70 grayscale-[30%]' : ''
                  } ${getPhaseBgColor(phaseIndex)}`}
                  onClick={handlePhaseClick}
                  data-testid={`phase-card-${phaseIndex + 1}`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${getPhaseColor(phaseIndex)} opacity-5`} />
                  <CardContent className="pt-4 relative">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${getPhaseColor(phaseIndex)}`}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex items-center gap-1">
                        {isCompleted && <Badge className="bg-emerald-500 text-xs">Completed</Badge>}
                        {isInProgress && <Badge className="bg-blue-500 text-xs">In Progress</Badge>}
                        {isAvailable && !isInProgress && !isCompleted && <Badge className="bg-amber-500 text-xs">Available</Badge>}
                        {isLocked && (
                          <>
                            <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600">Preview</Badge>
                            <Lock className="h-4 w-4 text-muted-foreground" />
                          </>
                        )}
                        {isCompleted && <CheckCircle2 className="h-5 w-5 text-emerald-600 ml-1" />}
                      </div>
                    </div>
                    <h3 className="font-semibold mb-1">Phase {phaseIndex + 1}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{advPhase.name}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>{progressText}</span>
                      <span>{Math.round(progressValue)}%</span>
                    </div>
                    <Progress value={progressValue} className="h-1.5" />
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Overall Certification Progress (if Phase 4+ complete) */}
          {certificationStatus?.phase_4_completed && (
            <Card className="mt-4 bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
              <CardContent className="py-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-cyan-600" />
                    <span className="text-sm font-medium">Overall Certification Progress</span>
                  </div>
                  <span className="text-sm text-muted-foreground">{certificationStatus?.overall_progress || 0}%</span>
                </div>
                <Progress value={certificationStatus?.overall_progress || 0} className="h-3" />
                {certificationStatus?.is_certified && (
                  <div className="flex items-center gap-2 mt-3 text-emerald-600">
                    <Award className="h-5 w-5" />
                    <span className="font-medium">You are a TekWissen Certified Recruiter!</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/rpat/simulations')} data-testid="simulations-card">
            <CardContent className="pt-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-cyan-100">
                <Target className="h-6 w-6 text-cyan-600" />
              </div>
              <div>
                <h3 className="font-semibold">Simulations</h3>
                <p className="text-sm text-muted-foreground">Practice real scenarios</p>
              </div>
              <ChevronRight className="ml-auto h-5 w-5 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/rpat/playbooks')}>
            <CardContent className="pt-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-violet-100">
                <BookOpen className="h-6 w-6 text-violet-600" />
              </div>
              <div>
                <h3 className="font-semibold">Playbooks</h3>
                <p className="text-sm text-muted-foreground">1,200+ pages of reference</p>
              </div>
              <ChevronRight className="ml-auto h-5 w-5 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/rpat/typing-tests')}>
            <CardContent className="pt-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-emerald-100">
                <Keyboard className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold">Typing Tests</h3>
                <p className="text-sm text-muted-foreground">Practice typing speed</p>
              </div>
              <ChevronRight className="ml-auto h-5 w-5 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/rpat/simulation-history')}>
            <CardContent className="pt-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-100">
                <History className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold">Simulation History</h3>
                <p className="text-sm text-muted-foreground">Review past feedback</p>
              </div>
              <ChevronRight className="ml-auto h-5 w-5 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/rpat/certificate')}>
            <CardContent className="pt-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-amber-100">
                <Award className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold">Certification</h3>
                <p className="text-sm text-muted-foreground">Get certified</p>
              </div>
              <ChevronRight className="ml-auto h-5 w-5 text-muted-foreground" />
            </CardContent>
          </Card>
        </div>

        {/* Admin Analytics Link */}
        {user?.role === 'admin' && (
          <Card className="bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200 hover:shadow-md transition-shadow cursor-pointer mt-6" onClick={() => navigate('/rpat/analytics')}>
            <CardContent className="pt-4 flex items-center gap-4">
              <div className="p-3 rounded-lg bg-slate-100">
                <BarChart3 className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <h3 className="font-semibold">Admin Analytics</h3>
                <p className="text-sm text-muted-foreground">View training program performance metrics</p>
              </div>
              <ChevronRight className="ml-auto h-5 w-5 text-muted-foreground" />
            </CardContent>
          </Card>
        )}
        
        {/* Category Selection Modal for Advanced Certification */}
        <JobCategorySelectionModal
          isOpen={showCategoryModal}
          onClose={() => setShowCategoryModal(false)}
          onCategorySelected={handleCategorySelected}
        />
      </div>
    </MainLayout>
  );
};

export default RPATDashboard;
